import { useActions, useSelector } from "app/utils/hooks";
import { RootState } from "typedefs";
import { PostingFormProps } from "./index.types";
import { useCallback, useEffect, useState } from "react";
import {
  clearMediaState,
  fetchMediaById,
} from "app/actions/media-library-data";
import { clearDestinations } from "app/actions/destinations";
import { PostingModel } from "app/models/PostModel";
import TagModel from "app/models/TagModel";
import { toInteger } from "lodash";
import { shallowEqual } from "react-redux";
import { useAttachments } from "app/utils/hooks/useAttachments";
import { MediaModel } from "app/models";

const INITIAL_POST_LOCATIONS = {
  smart_list_ids: [],
  space_id: [],
  team_ids: [],
  segment_ids: [],
  team_group_ids: [],
};

const usePostingForm = ({
  post,
  attachmentProps,
  onPost,
  onCancel,
  expanded,
  postTextIsRequired,
  locationsIsRequired,
  scheduleIsRequired,
  hasSchedule,
  scheduledInActive,
  isReply,
}: PostingFormProps) => {
  const resourceActions = useActions({
    fetchMediaById,
    clearMediaState,
    clearDestinations,
  });

  const { self } = useSelector((state: RootState) => state.user, shallowEqual);
  const selectedLocations = useSelector(
    (state: RootState) => state.destinations.selectedDestinations,
    shallowEqual,
  );

  const {
    attachments,
    onSelectMedia,
    onClearMedia,
    setAttachments,
    attachmentsTags,
  } = useAttachments({
    initialAttachments: attachmentProps || post?.resources,
    multiSelect: !isReply ?? false,
  });

  const [postTextError, setPostTextError] = useState<boolean>(false);
  const [postLocationsError, setPostLocationsError] = useState<boolean>(false);
  const [scheduleError, setScheduleError] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [postLocations, setPostLocations] = useState(INITIAL_POST_LOCATIONS);
  const [postingFormIsExpanded, setPostingFormIsExpanded] = useState<boolean>(
    expanded || false,
  );
  const [pickedSchedule, setPickedSchedule] = useState(null);
  const [newPostData, setNewPostData] = useState<PostingModel | null>(
    post || {
      details: "",
      tags: [],
      resource_ids: attachments?.map((a) => a.id),
      resource_type: attachments?.length === 1 ? attachments[0].type : null,
    },
  );
  const [paywallIsOpened, setPaywallIsOpened] = useState(false);

  const scheduleVisible = hasSchedule && self?.kind === "coach";

  const postingFormExpandHandler = () => {
    setPostingFormIsExpanded((prev) => !prev);
  };

  const postingFormShrinkHandler = useCallback(() => {
    setPostingFormIsExpanded(false);
  }, []);

  const paywallModalHandler = () => {
    setPaywallIsOpened((prev) => !prev);
  };

  const textAreaHandler = useCallback((text: string) => {
    setNewPostData((prevData) => ({
      ...prevData,
      details: text,
    }));
    if (text && text.trim() !== "" && postTextError) {
      setPostTextError(false);
    }
  }, [postTextError]);

  const onTagsChange = (tags: TagModel[]) => {
    if (tags) {
      const tagNames = tags.map((item: { name: string }) => item.name);
      setNewPostData((prevData: any) => ({
        ...prevData,
        tags: tagNames,
      }));
    }
  };

  const onScheduleChange = useCallback(
    (pickedAt: string, pickedTime?: "Days" | "Hours" | "Weeks") => {
      if (!pickedAt || parseInt(pickedAt, 10) === 0) {
        setPickedSchedule(null);
        setNewPostData((prevData: PostingModel) => ({
          ...prevData,
          scheduled_in: null,
        }));
        return;
      }
      setPickedSchedule(pickedAt);

      if (scheduledInActive) {
        let calculatedTime = toInteger(pickedAt);
        if (pickedTime === "Days") {
          calculatedTime = toInteger(pickedAt) * 24;
        } else if (pickedTime === "Weeks") {
          calculatedTime = toInteger(pickedAt) * 7 * 24;
        }

        setNewPostData((prevData: PostingModel) => ({
          ...prevData,
          scheduled_in: calculatedTime,
        }));
      } else {
        setNewPostData((prevData: PostingModel) => ({
          ...prevData,
          scheduled_at: new Date(pickedAt).toISOString(),
        }));
      }
    },
    [scheduledInActive],
  );

  const clearPostingForm = useCallback(() => {
    resourceActions.clearMediaState();
    resourceActions.clearDestinations();
    postingFormShrinkHandler();
    setErrorMessage(null);
    setPostTextError(false);
    setPostLocationsError(false);
    setScheduleError(false);
    setPostLocations(null);
    setNewPostData(null);
    setPickedSchedule(null);
    setAttachments([]);
  }, [resourceActions, postingFormShrinkHandler]);

  const onSubmitFunction = () => {
    if (postTextIsRequired || locationsIsRequired || scheduleIsRequired) {
      if (
        (!attachments || attachments.length === 0) &&
        postTextIsRequired &&
        (!newPostData?.details || newPostData.details.trim() === "")
      ) {
        setPostTextError(true);
        return;
      } else if (postTextError) {
        setPostTextError(false);
      }

      if (
        locationsIsRequired &&
        (!selectedLocations || !selectedLocations.length)
      ) {
        setPostLocationsError(true);
        return;
      } else if (postLocationsError) {
        setPostLocationsError(false);
      }

      if (scheduleIsRequired && !pickedSchedule) {
        setScheduleError(true);
        return;
      } else if (scheduleError) {
        setScheduleError(false);
      }
    }

    Promise.resolve(onPost(newPostData));
    clearPostingForm();
  };

  const onCancelHandler = () => {
    clearPostingForm();
    if (onCancel) {
      onCancel();
    }
  };

  const onReorderAttachments = (media: MediaModel[]) => {
    setAttachments(media);
  };

  useEffect(() => {
    if (attachmentProps) {
      setAttachments(attachmentProps);
      const propsTags = Array.isArray(attachmentProps)
        ? attachmentProps?.flatMap((att) => att?.tags || [])
        : [];

      const tagNames = propsTags?.map((item: any) => item?.name);
      setNewPostData((prevData: any) => ({
        ...prevData,
        tags: tagNames,
      }));
    }
  }, [attachmentProps, setAttachments]);

  useEffect(() => {
    if (!attachments || attachments.length === 0) {
      setNewPostData((prev) => ({
        ...prev,
        resource_ids: [],
        resource_type: null,
      }));
      return;
    }

    if (postTextError) {
      setPostTextError(false);
    }

    const resource_ids = attachments.map((a) => a.id);
    const resource_type = attachments.length === 1 ? attachments[0].type : null;
    const tags = attachmentsTags as any;

    setNewPostData((prevData) => {
      const prevIds = prevData?.resource_ids ? prevData.resource_ids : [];
      const alreadyExists = resource_ids?.some((id) => prevIds?.includes(id));

      if (alreadyExists) {
        const uniqueTags = [...new Set([...(prevData?.tags || []), ...tags])];
        return { ...prevData, resource_ids, tags: uniqueTags };
      }

      const uniqueTags = [...new Set([...(prevData?.tags || []), ...tags])];
      return {
        ...prevData,
        resource_ids,
        resource_type,
        tags: uniqueTags,
      };
    });
  }, [attachments, postTextError]);

  useEffect(() => {
    if (selectedLocations?.length > 0) {
      if (errorMessage) {
        setErrorMessage(null);
      }
      if (postLocationsError) {
        setPostLocationsError(false);
      }

      selectedLocations.forEach((obj: { id: string; object: string }) => {
        let locationKey = "team_group_ids";

        switch (obj.object) {
          case "smart_list":
            locationKey = "smart_list_ids";
            break;
          case "athlete_space":
            locationKey = "space_id";
            break;
          case "team":
            locationKey = "team_ids";
            break;
          case "segment":
            locationKey = "segment_ids";
            break;
        }

        setPostLocations((prevState: any) => ({
          ...(prevState ?? {}),
          [locationKey]: [...(prevState[locationKey] || []), obj.id],
        }));
      });
    }

    return () => {
      setPostLocations(INITIAL_POST_LOCATIONS);
    };
  }, [selectedLocations, errorMessage, postLocationsError]);

  useEffect(() => {
    setNewPostData((prevData) => ({
      ...prevData,
      ...postLocations,
    }));
  }, [postLocations]);

  return {
    postTextError,
    textAreaHandler,
    onTagsChange,
    onScheduleChange,
    onSelectMedia,
    onClearMedia,
    newPostData,
    postingFormExpandHandler,
    postingFormIsExpanded,
    pickedSchedule,
    onSubmitFunction,
    onCancelHandler,
    postLocationsError,
    scheduleError,
    attachments,
    paywallModalHandler,
    paywallIsOpened,
    scheduleVisible,
    onReorderAttachments,
  };
};

export default usePostingForm;
